import PageShell from "@/components/PageShell";
import PageHero from "@/components/PageHero";
import AIInfra from "@/components/AIInfra";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const REGISTRY = [
    { hash: "0xa2…f81e", arch: "llama-3.1-70b", params: "70B", visibility: "Public", owner: "0x84…D2a1", rev: "5%", gpu: "H100" },
    { hash: "0x91…b3c2", arch: "mixtral-8x22b", params: "176B", visibility: "Public", owner: "0xfa…21EE", rev: "5%", gpu: "H100" },
    { hash: "0xc4…2d09", arch: "sdxl-turbo", params: "2.6B", visibility: "Public", owner: "0x1c…77B0", rev: "3%", gpu: "A100" },
    { hash: "0x5e…908a", arch: "private-fin-v2", params: "13B", visibility: "Private", owner: "0xd0…3FAA", rev: "8%", gpu: "A100" },
];

const ROUTING = [
    { k: "Latency", v: "Pick lowest-latency capable node by region." },
    { k: "Cost", v: "Minimize $DCX/call within latency budget." },
    { k: "Capacity", v: "Spread load across available GPU clusters." },
    { k: "Reliability", v: "Prefer high reliability-score operators." },
    { k: "Data Locality", v: "Co-locate inference with storage shards." },
];

export default function AIComputePage() {
    return (
        <PageShell>
            <PageHero
                eyebrow="AI Compute"
                title="An Open GPU Network"
                accent="for the AI Economy."
                sub="DeCloudX creates a decentralized GPU layer where developers can host models, run inference, fine-tune workloads, and access distributed AI compute without relying only on centralized hyperscalers."
            />

            <AIInfra />

            <section className="relative py-16 md:py-24 border-t border-line">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <div className="overline mb-3">AI Model Registry</div>
                    <h2 className="font-heading font-black text-2xl sm:text-3xl text-ink max-w-3xl">Models are first-class on-chain objects.</h2>
                    <div className="mt-10 surface-strong rounded-2xl overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full" data-testid="ai-registry">
                                <thead>
                                    <tr className="border-b border-line bg-card-muted">
                                        {["Hash","Architecture","Params","Visibility","Owner","Rev Share","GPU"].map((h, i) => (
                                            <th key={i} className="text-left p-4 font-mono text-[10px] uppercase tracking-[0.2em] text-faint-ink whitespace-nowrap">{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {REGISTRY.map((r, i) => (
                                        <tr key={i} className="border-b border-line last:border-b-0 hover:bg-card-muted/50 transition-colors">
                                            <td className="p-4 text-xs text-brand-violet font-mono">{r.hash}</td>
                                            <td className="p-4 text-sm text-ink font-medium">{r.arch}</td>
                                            <td className="p-4 text-xs text-muted-ink font-mono">{r.params}</td>
                                            <td className="p-4 text-xs text-muted-ink">{r.visibility}</td>
                                            <td className="p-4 text-xs text-muted-ink font-mono">{r.owner}</td>
                                            <td className="p-4 text-xs text-brand-blue font-mono">{r.rev}</td>
                                            <td className="p-4 text-xs text-muted-ink">{r.gpu}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </section>

            <section className="relative py-16 md:py-24 border-t border-line">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <div className="overline mb-3">Inference Routing</div>
                    <h2 className="font-heading font-black text-2xl sm:text-3xl text-ink max-w-3xl">AI-powered scheduling balances five signals.</h2>
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-5 gap-3">
                        {ROUTING.map((r, i) => (
                            <div key={i} className="surface p-5 rounded-xl" data-testid={`route-${i}`}>
                                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-faint-ink">{String(i+1).padStart(2,"0")}</div>
                                <div className="font-heading font-bold text-base text-ink mt-1.5">{r.k}</div>
                                <p className="text-xs text-muted-ink mt-2 leading-relaxed">{r.v}</p>
                            </div>
                        ))}
                    </div>
                    <div className="mt-12">
                        <Link to="/contact?role=developer" className="btn-primary">Launch AI Workloads on DeCloudX <ArrowRight size={15} strokeWidth={2} /></Link>
                    </div>
                </div>
            </section>
        </PageShell>
    );
}
